<template>
  <div v-if="projectId > 0 && items.length" class="ai-todo-bar">
    <button type="button" class="ai-todo-bar-toggle" @click="expanded = !expanded">
      <span>待办任务（{{ items.length }}项）</span>
      <span class="ai-todo-bar-arrow" :class="{ open: expanded }" aria-hidden="true">»</span>
    </button>
    <ul v-if="expanded" class="ai-todo-list">
      <li v-for="item in items" :key="item.id" class="ai-todo-item">
        <div class="ai-todo-item-main">
          <strong>{{ item.fileName || item.objectKey }}</strong>
          <span>{{ item.applicantName || '同事' }} 申请{{ permLabel(item.permissionType) }}权限</span>
        </div>
        <div class="ai-todo-item-actions">
          <button type="button" class="ai-todo-link" :disabled="decidingId === item.id" @click="decide(item, true)">通过</button>
          <button type="button" class="ai-todo-link is-danger" :disabled="decidingId === item.id" @click="decide(item, false)">拒绝</button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import {
  decideDocumentPermission,
  listPendingDocumentPermissionRequests,
  type DocumentPermissionPendingRequest,
} from '@/api/documentPermissionApi';

const props = defineProps<{
  projectId: number;
}>();

const route = useRoute();
const items = ref<DocumentPermissionPendingRequest[]>([]);
const expanded = ref(false);
const decidingId = ref<number | null>(null);

function permLabel(type: string) {
  return String(type).toUpperCase() === 'EDIT' ? '编辑' : '下载';
}

async function loadTodos() {
  if (!(props.projectId > 0)) {
    items.value = [];
    return;
  }
  try {
    items.value = await listPendingDocumentPermissionRequests(props.projectId);
    const focusId = Number(route.query.docPermRequestId || 0);
    if (focusId > 0 && items.value.some((x) => Number(x.id) === focusId)) {
      expanded.value = true;
    }
  } catch {
    items.value = [];
  }
}

async function decide(item: DocumentPermissionPendingRequest, approved: boolean) {
  decidingId.value = item.id;
  try {
    await decideDocumentPermission(props.projectId, item.id, approved);
    ElMessage.success(approved ? '已通过权限申请' : '已拒绝权限申请');
    items.value = items.value.filter((x) => x.id !== item.id);
    if (!items.value.length) expanded.value = false;
  } catch (e: unknown) {
    ElMessage.error((e as Error)?.message || '审批失败');
  } finally {
    decidingId.value = null;
  }
}

watch(() => props.projectId, () => { void loadTodos(); });
watch(() => route.query.docPermRequestId, () => { void loadTodos(); });
watch(() => route.query.aiTodo, (v) => {
  if (String(v || '') === '1') expanded.value = true;
});

onMounted(() => {
  if (String(route.query.aiTodo || '') === '1' || route.query.docPermRequestId) {
    expanded.value = true;
  }
  void loadTodos();
});
</script>

<style scoped>
.ai-todo-bar {
  margin: 0 12px 8px;
  border: 1px solid #d6e4ff;
  border-radius: 8px;
  background: #f0f7ff;
  overflow: hidden;
  flex-shrink: 0;
}
.ai-todo-bar-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  color: #1677ff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ai-todo-bar-arrow {
  color: #8f959e;
  transform: rotate(90deg);
  transition: transform 0.15s ease;
}
.ai-todo-bar-arrow.open {
  transform: rotate(-90deg);
}
.ai-todo-list {
  list-style: none;
  margin: 0;
  padding: 0 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ai-todo-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff;
}
.ai-todo-item-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ai-todo-item-main strong {
  font-size: 13px;
  color: #1f2329;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-todo-item-main span {
  font-size: 12px;
  color: #8f959e;
}
.ai-todo-item-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.ai-todo-link {
  border: 0;
  background: transparent;
  color: #1677ff;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
}
.ai-todo-link.is-danger {
  color: #f56c6c;
}
.ai-todo-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
